import React, { useState } from 'react';
import {
  Box,
  Text,
  Heading,
  Button,
  CheckCircleIcon,
  EditIcon,
  ArrowLeftIcon,
  InfoGroup,
  InfoItem,
  InfoItemKey,
  InfoItemValue,
  Indicator,
  Link,
  StepItemIcon,
  useToast,
  Amount,
  Badge,
  AlertOnlyIcon,
} from '@razorpay/blade/components';
import {
  BorderedBox,
  FormFieldsWrapper,
  FormFooterWrapper,
  FormHeading,
  StyledForm,
} from '../components/styles';
import { getPendingFormFields } from './data';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { FloatingDiscardButton } from 'components/EmployeeOnboarding/components/FloatingDiscardButton';
import { useDraftEmployeeData } from './hooks/useDraftEmployeeData';
import { useMutation } from 'react-query';
import api from 'api';
import { useDraftEmployeeId } from './hooks/useDraftEmployeeId';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import { SuccessModal } from './components/SuccessModal';
import { ReviewDetailsFormFields, ReviewDetailItem } from './types';
import { ValidationSchema } from 'forms';
import {
  canCreateEmployee,
  getRequiredFieldValidationSchema,
  getReviewDetailsFormData,
  getReviewDetailsSections,
} from './utils';
import { useOfficeLocationById } from './hooks/useOfficeLocationById';
import useFeature from 'hooks/useFeature';

export const ReviewDetailsInfoValue = ({
  detailItem,
  formData,
  validationSchema,
}: {
  detailItem: ReviewDetailItem;
  formData: Partial<ReviewDetailsFormFields> | null;
  validationSchema: ValidationSchema<ReviewDetailsFormFields>;
}) => {
  const validatorFn = detailItem.fieldKey ? validationSchema[detailItem.fieldKey] : null;
  const hasError = validatorFn ? Boolean(validatorFn(formData || {})) : false;

  if (detailItem.shouldHide) {
    return null;
  }

  if (hasError) {
    return (
      <InfoItem key={detailItem.label}>
        <InfoItemKey leading={() => <StepItemIcon icon={AlertOnlyIcon} color="notice" />}>
          {detailItem.label}
        </InfoItemKey>
        <InfoItemValue>
          <Badge color="negative">Required</Badge>
        </InfoItemValue>
      </InfoItem>
    );
  }

  return (
    <InfoItem key={detailItem.label}>
      <InfoItemKey leading={() => <StepItemIcon icon={CheckCircleIcon} color="positive" />}>
        {detailItem.label}
      </InfoItemKey>
      <InfoItemValue>
        {detailItem.type === 'amount' ? (
          <Amount value={Number(detailItem.value)} currency="INR" suffix="decimals" />
        ) : (
          <Text weight="regular">{detailItem.value || '-'}</Text>
        )}
      </InfoItemValue>
    </InfoItem>
  );
};

export const ReviewDetails = ({
  setShowDiscardModal,
  setShowSaveDraftAndCloseModal,
}: {
  setShowDiscardModal: (value: boolean) => void;
  setShowSaveDraftAndCloseModal: (value: boolean) => void;
}) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const toast = useToast();
  const { draftEmployeeId } = useDraftEmployeeId();
  const history = useHistory();
  const { isEsicMultiLocationEnabled } = useFeature('esicMultiLocation');
  const { data, isLoading, isFetching } = useDraftEmployeeData();

  const { data: selectedOfficeLocation, isFetchingOfficeLocation } = useOfficeLocationById(
    data?.basicDetails?.officeLocationId,
  );

  const formData = data ? getReviewDetailsFormData(data, isEsicMultiLocationEnabled) : null;
  const validationSchema = getRequiredFieldValidationSchema(!data?.basicDetails?.emailNotPresent);
  const isSendInviteButtonDisabled = formData
    ? !canCreateEmployee(formData, validationSchema)
    : true;

  const moveToPeopleOrDetailPage = (peopleId?: number) => {
    if (peopleId) {
      window.location.href = `${routePaths.employeeDetails.root}${peopleId}`;
    } else {
      history.push(routePaths.peopleV2);
    }
  };

  const {
    mutate: createEmployee,
    data: createEmployeeData,
    isLoading: isSubmitting,
  } = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.createEmployee,
    onSuccess: (response) => {
      if (response.payrollCreateEmployee?.data?.inviteSent) {
        setShowSuccessModal(true);
      } else if (response.payrollCreateEmployee?.data?.peopleId) {
        toast.show({
          content: 'Employee created successfully',
          color: 'positive',
        });
        moveToPeopleOrDetailPage(response.payrollCreateEmployee?.data?.peopleId);
      } else {
        toast.show({
          content: 'Failed to create employee',
          color: 'negative',
        });
      }
    },
    onError: (error: typeof AppError) => {
      toast.show({
        content: parseGraphqlErrorFromExtensions(error),
        color: 'negative',
      });
    },
  });

  const handleSaveAndClose = () => {
    setShowSaveDraftAndCloseModal(true);
  };

  const handleBack = () => {
    history.push({
      pathname: routePaths.employeeOnboarding.employee.compliance,
      search: `?draftId=${draftEmployeeId}`,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createEmployee({
      id: draftEmployeeId,
      emailNotPresent: data?.basicDetails?.emailNotPresent ?? false,
    });
  };

  const shouldDisableButtons = isLoading || isFetching || isSubmitting || isFetchingOfficeLocation;

  const shouldShowLoader = isLoading || isFetching || isFetchingOfficeLocation;

  const hasEmail = !data?.basicDetails?.emailNotPresent;

  const sections = getReviewDetailsSections(data, selectedOfficeLocation);

  return (
    <>
      <FloatingDiscardButton
        isDisabled={shouldDisableButtons}
        handleDiscardModal={() => setShowDiscardModal(true)}
      />
      <StyledForm onSubmit={handleSubmit}>
        {/* Header Section */}
        <FormFieldsWrapper shouldShowSpinner={shouldShowLoader}>
          <FormHeading
            title="Review Details"
            description="If the details look good, invite them now. Only invite this employee if you're ready to start the onboarding process."
          />

          {/* Content Sections */}
          {sections.map((section) => (
            <BorderedBox key={section.title}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Heading size="medium" color="surface.text.gray.normal" weight="medium">
                  {section.title}
                </Heading>
                <Link
                  variant="button"
                  icon={EditIcon}
                  iconPosition="left"
                  onClick={() =>
                    history.push({
                      pathname: section.stepRoute,
                      search: `?draftId=${draftEmployeeId}`,
                    })
                  }>
                  Edit
                </Link>
              </Box>

              <InfoGroup
                itemOrientation="horizontal"
                size="medium"
                valueAlign="right"
                maxWidth="100%">
                {section.items.map((item) => (
                  <ReviewDetailsInfoValue
                    key={item.label}
                    detailItem={item}
                    formData={formData}
                    validationSchema={validationSchema}
                  />
                ))}
              </InfoGroup>
            </BorderedBox>
          ))}

          {/* Pending Items Section */}
          {hasEmail && (
            <BorderedBox>
              <Box>
                <Heading size="medium" color="surface.text.gray.normal" weight="medium">
                  Here's what we still need from {data?.basicDetails?.name || 'employee'}
                </Heading>
                <Text size="small" weight="regular" color="surface.text.gray.muted">
                  Once {data?.basicDetails?.name || 'employee'} is invited, they'll be prompted to
                  provide these details.
                </Text>
              </Box>

              <Box
                display="grid"
                gridTemplateColumns={{ base: '1fr', m: '1fr 1fr' }}
                gap="spacing.5">
                {getPendingFormFields(data).map((item) => (
                  <Box key={item} display="flex" alignItems="center" gap="spacing.3">
                    <Indicator
                      color="primary"
                      emphasis="intense"
                      size="small"
                      accessibilityLabel={`${item} pending`}
                    />
                    <Text color="surface.text.gray.subtle">{item}</Text>
                  </Box>
                ))}
              </Box>
            </BorderedBox>
          )}
        </FormFieldsWrapper>

        <FormFooterWrapper>
          <Button
            variant="tertiary"
            size="medium"
            icon={ArrowLeftIcon}
            iconPosition="left"
            onClick={handleBack}
            isDisabled={shouldDisableButtons}>
            Back
          </Button>
          <Box display="flex" gap="spacing.3">
            <Button
              variant="secondary"
              size="medium"
              onClick={handleSaveAndClose}
              isLoading={shouldDisableButtons}>
              Save and Close
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="medium"
              isLoading={shouldDisableButtons}
              isDisabled={isSendInviteButtonDisabled}>
              {data?.basicDetails?.emailNotPresent ? 'Continue' : 'Send Invite'}
            </Button>
          </Box>
        </FormFooterWrapper>
      </StyledForm>
      <SuccessModal
        isOpen={showSuccessModal}
        onDismiss={() =>
          moveToPeopleOrDetailPage(createEmployeeData?.payrollCreateEmployee?.data?.peopleId)
        }
        employeeName={data?.basicDetails?.name || ''}
      />
    </>
  );
};
